import React, { useState, useEffect } from 'react';

import './ProductPage.css';
import RadixLogo from '../../../../../assets/RadixProduct.webp';
import NoOfUser from '../../../../../assets/NoOfUser.svg';
import Tick from '../../../../../assets/Tick.svg';
import blueTick from '../../../../../assets/blueTick.svg';
import FeatureTick from '../../../../../assets/FeatureTick.svg';
import PopupForm from '../PopupForm/PopupForm'; 
import RedirectionCard from '../RedirectionCard/RedirectionCard';
import { fetchMenus, fetchSubMenus } from '../../../../../services/radix/radixServices';

const ProductPage = () => {
  const [selectedMenu, setSelectedMenu] = useState({});
  const [selectedSubMenu, setSelectedSubMenu] = useState('');
  const [detail, setDetail] = useState({
    title: '',
    content: '',
    basePrice: 0,
    featurePackPrice: 0,
    featurePackUserPrice: 0,
    additionalUserPrice: 0,
  });
  const [userCount, setUserCount] = useState(1);
  const [isFeaturePackChecked, setIsFeaturePackChecked] = useState(false);
  const [isFeaturePackDisabled, setIsFeaturePackDisabled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isRedirectionCardVisible, setIsRedirectionCardVisible] = useState(false);
  const [menuData, setMenuData] = useState([]);
  const [subMenuData, setSubMenuData] = useState([]);

  useEffect(() => {
    const loadMenuData = async () => {
      const data = await fetchMenus();
      setMenuData(data);
      if (data && data.length > 0) {
        setSelectedMenu(data[0]); // Select the first menu by default
      }
    };

    const loadSubMenuData = async () => {
      const data = await fetchSubMenus();
      setSubMenuData(data);
    };

    loadMenuData();
    loadSubMenuData();
  }, []);

  useEffect(() => {
    if (selectedMenu && selectedMenu.radix_submenus && selectedMenu.radix_submenus.length > 0) {
      setSelectedSubMenu(selectedMenu.radix_submenus[0].name);
    } else {
      setSelectedSubMenu('');
      setDetail({
        title: '',
        content: '',
        basePrice: 0,
        featurePackPrice: 0,
        featurePackUserPrice: 0,
        additionalUserPrice: 0,
      });
    }
  }, [selectedMenu]);

  useEffect(() => {
    if (selectedSubMenu) {
      const submenuDetails = subMenuData.find(item => item.name === selectedSubMenu);
      if (submenuDetails) {
        setDetail({
          title: submenuDetails.name,
          content: submenuDetails.productName || '',
          basePrice: submenuDetails.basePrice || 0,
          featurePackPrice: submenuDetails.featurePackPrice || 0,
          featurePackUserPrice: submenuDetails.featurePackUserPrice || 0,
          additionalUserPrice: submenuDetails.additionalUserPrice || 0,
        });

        const isFeaturePackRequired = [
          'Yarn Manufacturer', 'Grey Manufacturer', 'Dyeing and printing Mills',
          'Dyeing and printing Mills with Payroll', 'Digital Printing Units', 'Embroidery Units',
          'Handwork Units', 'Yarn Traders',
        ].includes(selectedSubMenu);

        setIsFeaturePackChecked(isFeaturePackRequired);
        setIsFeaturePackDisabled(isFeaturePackRequired);
      }
    }
  }, [selectedSubMenu, subMenuData]);

  const handleMenuSelect = (menuItem) => {
    setSelectedMenu(menuItem);
  };

  const handleSubMenuSelect = (subMenuItem) => {
    setSelectedSubMenu(subMenuItem);
  };

  const handleFeaturePackCheckboxChange = (event) => {
    setIsFeaturePackChecked(event.target.checked);
  };

  const incrementUserCount = () => {
    setUserCount(userCount + 1);
  };

  const decrementUserCount = () => {
    if (userCount > 1) {
      setUserCount(userCount - 1);
    }
  };

  const calculateSubtotal = () => {
    let subtotal = detail.basePrice;
    if (isFeaturePackChecked) {
      subtotal += detail.featurePackPrice;
    }
    return subtotal;
  };

  const calculateExtraUserCharges = () => {
    if (userCount > 1) {
      const extraUserPrice = isFeaturePackChecked ? detail.featurePackUserPrice : detail.additionalUserPrice;
      return (userCount - 1) * extraUserPrice;
    }
    return 0;
  };

  const calculateTotalPrice = () => {
    const subtotal = calculateSubtotal();
    const extraUserCharges = calculateExtraUserCharges();
    const gst = (subtotal + extraUserCharges) * 0.18;
    return subtotal + extraUserCharges + gst;
  };

  const Header = () => {
    return (
      <header className="header-h1 mobile-margin">
        <h1>Choose Your Business Segment</h1>
      </header>
    );
  };

  const handleDownloadDemoClick = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleDownload = async (selectedMenu, selectedSubMenu) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_CONNECTION_URL}/download-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selectedMenu,
          selectedSubMenu,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download the file.');
      }

      const { downloadUrl } = await response.json();

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      link.remove();

      setIsRedirectionCardVisible(true);
      handleClosePopup();

    } catch (error) {
      console.error('Error:', error.message);
      alert(error.message);
    }
  };

  return (
    <>
      <Header />
      <div className="product-page">
        <div className="menu menu-center-alignment">
          <select
            className="menu-dropdown"
            value={selectedMenu.menu || ''}
            onChange={(e) => handleMenuSelect(menuData.find(item => item.menu === e.target.value))}
          >
            {menuData.map((item, index) => (
              <option key={index} value={item.menu}>
                {item.menu}
              </option>
            ))}
          </select>
          {menuData.map((item, index) => (
            <div
              key={index}
              className={`menu-item ${item.menu === selectedMenu.menu ? 'active' : ''}`}
              onClick={() => handleMenuSelect(item)}
            >
              {item.menu}
            </div>
          ))}
        </div>
        {selectedMenu && selectedMenu.radix_submenus && (
          <div className="submenu menu-center-alignment">
            <select
              className="submenu-dropdown"
              value={selectedSubMenu}
              onChange={(e) => handleSubMenuSelect(e.target.value)}
            >
              {selectedMenu.radix_submenus.map((item, index) => (
                <option key={index} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
            {selectedMenu.radix_submenus.map((item, index) => (
              <div
                key={index}
                className={`submenu-item ${item.name === selectedSubMenu ? 'active' : ''}`}
                onClick={() => handleSubMenuSelect(item.name)}
              >
                {item.name}
              </div>
            ))}
          </div>
        )}
        <div className="details">
          {detail.title && (
            <>
              <img src={RadixLogo} className="radixlogo" alt="Radix Logo" />
              <h2 style={{fontSize:'1.8rem',fontWeight:'500'}}>Get Your Price Estimate</h2>
              <h5 style={{fontSize:'1.1rem'}}>({detail.title})</h5>
              <div className='softwaredetails'>
                <h3 className='softwareinfo'>{detail.content}</h3>
                <div className="feature-pack">
                  <h3>
                    <input
                      type="checkbox"
                      checked={isFeaturePackChecked}
                      disabled={isFeaturePackDisabled}
                      onChange={handleFeaturePackCheckboxChange}
                    />
                    Feature Pack
                  </h3>
                  <ul>
                    <li><img className='featureticks' src={isFeaturePackChecked ? FeatureTick : Tick} alt="Feature" />2000 API for e-Way bill & e-Invoice</li>
                    <li><img className='featureticks' src={isFeaturePackChecked ? FeatureTick : Tick} alt="Feature" />Fetch Party details by entering GSTIN</li>
                    <li><img className='featureticks' src={isFeaturePackChecked ? FeatureTick : Tick} alt="Feature" />Third-Party message integration</li>
                    <li><img className='featureticks' src={isFeaturePackChecked ? FeatureTick : Tick} alt="Feature" />ReflectR Mobile App</li>
                  </ul>
                </div>
              
              <div className="users">
                  <img src={NoOfUser} />
                  <span>Add Users.  </span>
                  <span onClick={decrementUserCount}>−</span>
                  <span className="user-count">{userCount}</span>
                  <span onClick={incrementUserCount}>+</span>
                </div>
                <div className='summaryinfo'>
                  <ul>
                    <li>
                      <span>Product Subtotal: </span>
                      <span>₹{calculateSubtotal()}</span>
                    </li>
                    <li>
                      <span>Feature Pack </span>
                      <span>₹{isFeaturePackChecked ? detail.featurePackPrice : 0}</span>
                    </li>
                    <li>
                      <span>Extra user(s) Charges</span>
                      <span>₹{calculateExtraUserCharges()}</span>
                    </li>
                    <div className='gstlist'>
                      +18% GST 
                      (INR {(calculateSubtotal() + calculateExtraUserCharges()) * 0.18})
                    </div>
                    {/* <li className="total-amount">
                      <b>
                        <span>Total Amount: </span>
                      </b>
                      <span>₹{calculateTotalPrice()}</span>
                    </li> */}
                  </ul>
                </div>
                <div className="buy-now">
                  {/* <button>Buy Now</button> */}
                  <div className="download-demo">
                    <button onClick={handleDownloadDemoClick}>Download Demo</button>
                  </div>
                </div>
                <p id='product-page-bluetick'><img className="bluetick" src={blueTick} /> 30-day money-back guarantee*</p>
              </div>
            </>
          )}
          {showPopup && <PopupForm onClose={handleClosePopup} onDownload={handleDownload} selectedMenu={selectedMenu.menu} selectedSubMenu={selectedSubMenu} />} {/* Pass selected menu and submenu */}
      {showPopup && <div className="popup-overlay" onClick={handleClosePopup}></div>}
      {isRedirectionCardVisible && <RedirectionCard onClose={() => setIsRedirectionCardVisible(false)} />}
        </div>
      </div>
    </>
  );
};

export default ProductPage;
